import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, InputNumber, Switch, message, Space, Tag, Popconfirm, Empty, Tooltip, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CloudSyncOutlined, SyncOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import sslService from '../../services/sslService';
import I18nFieldTabs, { parseI18n, cleanI18n, mergeI18n } from '../../components/I18nFieldTabs';

const { Option } = Select;

const fmtTime = (v) => (v ? String(v).replace('T', ' ').slice(0, 16) : '-');

const SslProducts = () => {
  const { t } = useTranslation();
  const [priceSync, setPriceSync] = useState(null);
  const [priceSyncing, setPriceSyncing] = useState(false);
  const [defaults, setDefaults] = useState(null);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [defaultsForm] = Form.useForm();
  const [products, setProducts] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const [filters, setFilters] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchProviders();
    fetchProducts();
    fetchPriceSync();
  }, []);

  const fetchPriceSync = async () => {
    try {
      const r = await sslService.getPriceSources();
      if (r.success) setPriceSync((r.data || []).find((s) => s.key === 'ssl')?.last_run || null);
      const d = await sslService.getPricingDefaults();
      if (d.success) setDefaults(d.data);
    } catch (e) { /* status is optional */ }
  };

  const saveDefaults = async (v) => {
    try {
      const r = await sslService.savePricingDefaults(v);
      if (r.success) {
        setDefaults(r.data);
        setDefaultsOpen(false);
        message.success(t('Saved'));
        fetchProducts(pagination.current, pagination.pageSize, filters);
      }
    } catch (e) {
      message.error(e.error || e.message || t('Save failed'));
    }
  };

  const review = async (id, action) => {
    try {
      await sslService.reviewProductPrice(id, action);
      fetchProducts(pagination.current, pagination.pageSize, filters);
    } catch (e) {
      message.error(e.error || e.message || t('Operation failed'));
    }
  };

  const handlePriceSync = async () => {
    setPriceSyncing(true);
    try {
      const r = await sslService.runPriceSource('ssl');
      if (r.success) {
        message.success(t('Prices synced'));
        fetchPriceSync();
        fetchProducts(pagination.current, pagination.pageSize, filters);
      } else {
        message.error(r.error || r.message || t('Sync failed'));
      }
    } catch (error) {
      message.error(error.error || error.message || t('Sync failed'));
      fetchPriceSync();
    } finally {
      setPriceSyncing(false);
    }
  };

  const ruleWatch = Form.useWatch('pricing_rule', form);

  const fetchProviders = async () => {
    try {
      const response = await sslService.getProviders(true); // Admin mode
      if (response.success) {
        setProviders(response.data);
      }
    } catch (error) {
      message.error('Failed to fetch providers');
      console.error('Failed to fetch providers:', error);
    }
  };

  const fetchProducts = async (page = 1, pageSize = 50, filterParams = {}) => {
    setLoading(true);
    try {
      const response = await sslService.getProducts(true); // Admin mode
      if (response.success) {
        setProducts(response.data);
        // 注意：如果后端没有返回分页信息，使用默认值
        setPagination({
          current: response.meta?.current_page || page,
          pageSize: response.meta?.per_page || pageSize,
          total: response.meta?.total || response.data?.length || 0,
        });
      }
    } catch (error) {
      message.error('Failed to fetch products');
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingProduct(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    form.setFieldsValue({ ...product, i18n: parseI18n(product.i18n) });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await sslService.deleteProduct(id);
      message.success('Product deleted');
      fetchProducts(pagination.current, pagination.pageSize, filters);
    } catch (error) {
      message.error('Failed to delete');
    }
  };

  const handleSyncGoGetSSL = async () => {
    setSyncing(true);
    try {
      const response = await sslService.syncGoGetSSLProducts();
      if (response.success) {
        const d = response.data || {};
        message.success(`同步完成：新增 ${d.synced ?? 0}，更新 ${d.updated ?? 0}，失败 ${d.errors ?? 0}`);
        fetchProducts(pagination.current, pagination.pageSize, filters);
      } else {
        message.error(response.error || response.message || '同步失败');
      }
    } catch (error) {
      message.error('同步失败：' + (error.error || error.message || '未知错误'));
    } finally {
      setSyncing(false);
    }
  };

  const handleSubmit = async (formValues) => {
    // keep stored translations this form does not show (features)
    const values = { ...formValues, i18n: cleanI18n(mergeI18n(editingProduct?.i18n, formValues.i18n)) };
    try {
      if (editingProduct) {
        await sslService.updateProduct(editingProduct.id, values);
        message.success('Product updated');
      } else {
        await sslService.createProduct(values);
        message.success('Product created');
      }
      setModalVisible(false);
      fetchProducts(pagination.current, pagination.pageSize, filters);
    } catch (error) {
      message.error('Failed to save');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: 'Product',
      key: 'product',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.name}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.brand} • {record.type}</div>
        </div>
      ),
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Price',
      key: 'price',
      render: (_, record) => `${record.currency} ${record.sale_price}`,
    },
    {
      title: t('GoGetSSL cost'),
      key: 'cost',
      render: (_, r) => (r.cost == null ? '-' : `${r.cost_currency || ''} ${r.cost}`),
    },
    {
      title: t('Margin %'),
      key: 'margin',
      render: (_, r) => (r.margin_pct == null ? '-'
        : <Tag color={r.margin_pct <= 0 ? 'red' : r.margin_pct < 10 ? 'orange' : 'green'}>{r.margin_pct}%</Tag>),
    },
    {
      title: t('Pricing rule'),
      key: 'rule',
      render: (_, r) => (r.cost == null ? '-'
        : r.pricing_rule === 'markup'
          ? `${t('Cost + markup')} ${r.markup_percentage ?? defaults?.markup_percentage ?? ''}% / ≥${r.markup_min ?? defaults?.markup_min ?? ''}`
          : t('Manual price')),
    },
    {
      title: t('Last sync'),
      dataIndex: 'catalog_synced_at',
      key: 'synced',
      render: fmtTime,
    },
    {
      title: t('Price alert'),
      key: 'alert',
      render: (_, r) => {
        const reasons = (r.price_alert?.reasons || []).join('；');
        if (r.price_review) {
          return (
            <Space size={4}>
              <Tooltip title={reasons}><Tag color="blue">{t('Cost drop to review')}</Tag></Tooltip>
              <Button size="small" type="link" onClick={() => review(r.id, 'approve')}>{t('Approve')}</Button>
              <Button size="small" type="link" danger onClick={() => review(r.id, 'reject')}>{t('Reject')}</Button>
            </Space>
          );
        }
        if (r.loss_guard) return <Tooltip title={reasons}><Tag color="red">{t('Auto-deactivated (below cost)')}</Tag></Tooltip>;
        if (r.price_alert) return <Tooltip title={reasons}><Tag color={r.price_alert.level === 'critical' ? 'red' : 'orange'}>{t('Check price')}</Tag></Tooltip>;
        return '-';
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => <Tag color={active ? 'green' : 'red'}>{active ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>Edit</Button>
          <Popconfirm
            title="删除产品"
            description="删除后用户将无法再购买此产品，确定删除吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="删除"
            okButtonProps={{ danger: true }}
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="SSL 产品"
        extra={
          <Space wrap>
            {priceSync && (
              <Tooltip title={(priceSync.errors || []).join('; ')}>
                <Typography.Text type={priceSync.ok ? 'secondary' : 'danger'}>
                  {t('Last sync')}: {fmtTime(priceSync.at)}{priceSync.ok ? '' : ` · ${t('Sync failed')}`}
                </Typography.Text>
              </Tooltip>
            )}
            <Button onClick={() => { defaultsForm.setFieldsValue(defaults || {}); setDefaultsOpen(true); }}>{t('Pricing defaults')}</Button>
            <Button icon={<SyncOutlined />} loading={priceSyncing} onClick={handlePriceSync}>{t('Sync now')}</Button>
            <Popconfirm
              title="从 GoGetSSL 同步产品目录"
              description="将拉取 GoGetSSL 完整产品目录并新增/更新本地产品，可能需要一些时间。"
              onConfirm={handleSyncGoGetSSL}
              okText="开始同步"
              cancelText="取消"
            >
              <Button icon={<CloudSyncOutlined />} loading={syncing}>同步 GoGetSSL</Button>
            </Popconfirm>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>Add</Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={products}
          loading={loading}
          rowKey="id"
          pagination={pagination}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无产品，点击右上角 Add 或同步 GoGetSSL 产品目录" /> }}
        />
      </Card>

      <Modal
        title={editingProduct ? 'Edit Product' : 'Add Product'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="provider" label="Provider" rules={[{ required: true }]}>
            <Select>
              {providers.map(p => <Option key={p.provider} value={p.provider}>{p.name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="product_code" label="Product Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <I18nFieldTabs
            fields={[{ key: 'name', label: 'Name' }, { key: 'description', label: '描述', type: 'textarea' }]}
            base={<>
              <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="description" label="描述">
                <Input.TextArea rows={2} />
              </Form.Item>
            </>}
          />
          <Form.Item name="brand" label="Brand" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="type" label="Type" rules={[{ required: true }]}>
            <Select>
              <Option value="DV">DV</Option>
              <Option value="OV">OV</Option>
              <Option value="EV">EV</Option>
              <Option value="Wildcard">Wildcard</Option>
            </Select>
          </Form.Item>
          <Form.Item name="sale_price" label="Price">
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} disabled={ruleWatch === 'markup'} />
          </Form.Item>
          {editingProduct?.cost != null && (
            <Space align="start" wrap>
              <Form.Item name="pricing_rule" label={t('Pricing rule')} initialValue="manual">
                <Select style={{ width: 200 }}>
                  <Option value="manual">{t('Manual price')}</Option>
                  <Option value="markup">{t('Cost + markup')}</Option>
                </Select>
              </Form.Item>
              {ruleWatch === 'markup' && (
                <>
                  <Form.Item name="markup_percentage" label={t('Markup %')}
                    tooltip={t('Retail = cost + max(cost × markup, minimum profit), rounded up to .99. Empty = default')}>
                    <InputNumber min={0} max={1000} step={1} style={{ width: 140 }} placeholder={String(defaults?.markup_percentage ?? '')} />
                  </Form.Item>
                  <Form.Item name="markup_min" label={t('Minimum profit')}>
                    <InputNumber min={0} step={0.5} style={{ width: 140 }} placeholder={String(defaults?.markup_min ?? '')} />
                  </Form.Item>
                </>
              )}
            </Space>
          )}
          <Form.Item name="validity_period_days" label="Validity (Days)" rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title={t('Pricing defaults')} open={defaultsOpen} onCancel={() => setDefaultsOpen(false)} onOk={() => defaultsForm.submit()}>
        <Form form={defaultsForm} layout="vertical" onFinish={saveDefaults}>
          <Form.Item name="markup_percentage" label={t('Markup %')}><InputNumber min={0} max={1000} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="markup_min" label={t('Minimum profit')}><InputNumber min={0} step={0.5} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="review_pct" label={t('Cost drop needing review %')}><InputNumber min={0} max={100} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="sync_enabled" label={t('Daily price sync')} valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SslProducts;
